import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import TaskAssignModal from "./TaskAssignModal";
import RingNowDialog from "./RingNowDialog";
import TaskCancelModal from "./TaskCancelModal";
import TaskReleaseModal from "./TaskReleaseModal";
import TaskStartModal from "./TaskStartModal";
import Handler from "../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../Helpers/Utils";
import { Skeleton, DetailCard } from "../../Components/Needed";
import Timeline from "../../Components/Timeline";
import TaskTrackingPanel from "./TaskTrackingPanel";
import TaskCompleteModal from "./TaskCompleteModal";
import { useAuth } from "../../Contexts/AuthenticateProvider";
import { FaCarSide, FaArrowLeft, FaCheckCircle, FaMinusCircle, FaClock, FaTimesCircle, FaUserCheck, FaHourglassHalf, FaCamera, FaRegClipboard } from "react-icons/fa";
import { MdOutlineLocalShipping, MdOutlineAssignmentReturn } from "react-icons/md";
import { IoLocation } from "react-icons/io5";
import { listBackPath } from "../../Helpers/useListParams";

const STATUS_META = {
    pending: { label: "Pending", className: "badge-warning", icon: <FaHourglassHalf /> },
    assigned: { label: "Assigned", className: "badge-verified", icon: <FaUserCheck /> },
    progress: { label: "In progress", className: "badge-warning", icon: <FaClock /> },
    completed: { label: "Completed", className: "badge-active", icon: <FaCheckCircle /> },
    cancel: { label: "Cancelled", className: "badge-inactive", icon: <FaTimesCircle /> },
};
const PHOTO_SECTIONS = [
    { key: "startImages", title: "Photos at start" },
    { key: "endImages", title: "Photos at completion" },
];

const Badge = ({ text, icon, className = "" }) => (
    <span className={`custom-badge text-capitalize ${className}`}>{icon} {text}</span>
);

const Field = ({ label, children }) => (
    <div className="info-field">
        <div className="info-field-label">{label}</div>
        <div className="text-capitalize">{children || "-"}</div>
    </div>
);

// Google Maps link for a delivery/pick-up point: exact pin when lat/lng were stored, address search otherwise.
const mapsUrl = (point) => {
    const lat = Number(point?.lat ?? point?.location?.lat);
    const lng = Number(point?.lng ?? point?.location?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    const text = point?.address || point?.location?.address;
    return text ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}` : "";
};

const TaskDetail = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { hasPermission, profile } = useAuth();
    const [task, setTask] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightbox, setLightbox] = useState(null);
    const [modal, setModal] = useState(null); // assign | start | complete | cancel | release
    const [acting, setActing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [detail, log] = await Promise.all([
            Handler({ method: "get", url: `/staff/task/${taskId}` }),
            Handler({ method: "get", url: `/staff/task/${taskId}/log`, params: { limit: 100 } }),
        ]);
        setTask(detail?.success ? (detail?.data?.data || null) : null);
        setLogs(log?.success ? (log?.data?.data || []) : []);
        setLoading(false);
    }, [taskId]);

    useEffect(() => { load(); }, [load]);

    const runAction = async (url, successMessage) => {
        setActing(true);
        const response = await Handler({ method: "patch", url });
        if (response?.success) {
            toast.success(response?.data?.message || successMessage);
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Action failed"));
        }
        setActing(false);
    };
    const closeModal = () => setModal(null);
    const savedModal = () => { setModal(null); load(); };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={64} className="mb-3 rounded-4" />
                <Skeleton height={22} width="30%" className="mb-2" />
                <Skeleton height={14} width="45%" className="mb-4" />
                <Skeleton height={140} className="mb-3" />
                <Skeleton height={220} />
            </div>
        );
    }

    if (!task) {
        return (
            <div className="settings-card text-center py-5">
                <FaMinusCircle size={28} className="text-muted mb-3" />
                <p className="text-muted mb-3">Task not found.</p>
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(listBackPath("/tasks"))}>Back to tasks</button>
            </div>
        );
    }

    // Completion photos live in endImages; tasks finished before that field existed kept them in proofImages.
    const photos = { startImages: task.startImages || [], endImages: [...(task.endImages || []), ...(task.proofImages || [])] };
    const isDelivery = String(task.taskType).toLowerCase() === "delivery";
    const status = STATUS_META[task.status] || { label: task.status, className: "badge-warning", icon: null };
    const booking = task.booking;
    const address = isDelivery ? booking?.delivery : booking?.pickup;
    const isMine = !!task.staffId && task.staffId === profile?.staffId;
    const canAccept = hasPermission("task.accept");
    const canRun = hasPermission("task.run");
    const canAssign = hasPermission("task.assign");
    // Cancelling ends the customer's delivery/pickup, so only supervisors do it; staff use Release.
    const canCancel = hasPermission("task.cancel") && canAssign;
    const codeRequired = task.handoverCodeRequired === true;
    const btn = (label, cls, onClick, blocked = false) => <button key={label} type="button" className={`btn btn-sm ${cls}`} disabled={acting || blocked} title={blocked ? task.kycBlockReason : undefined} onClick={onClick}>{label}</button>;
    // KYC before delivery (D7): an open delivery can't start or complete until the customer's KYC is verified.
    const kycBlocked = task.kycPending === true;
    const actionItems = [];
    if (task.status === "pending") {
        if (canAccept) actionItems.push(btn("Accept", "btn-primary", () => runAction(`/staff/task/accept/${task.taskId}`, "Task accepted")));
        if (canAssign) actionItems.push(btn("Assign", "btn-outline-primary", () => setModal("assign")));
        if (canAssign) actionItems.push(btn("Ring now", "btn-outline-primary", () => setModal("ring")));
    }
    if (task.status === "assigned") {
        if (canRun && isMine) actionItems.push(btn("Start", "btn-primary", () => setModal("start"), kycBlocked));
        if (canAssign) actionItems.push(btn("Reassign", "btn-outline-primary", () => setModal("assign")));
        if (canAssign) actionItems.push(btn("Ring now", "btn-outline-primary", () => setModal("ring")));
        if (canAccept && isMine) actionItems.push(btn("Release", "btn-outline-warning", () => setModal("release")));
        if (canCancel) actionItems.push(btn("Cancel", "btn-outline-danger", () => setModal("cancel")));
    }
    if (task.status === "progress") {
        if ((canRun && isMine) || hasPermission("task.override")) actionItems.push(btn("Complete", "btn-success", () => setModal("complete"), kycBlocked));
        if (canCancel) actionItems.push(btn("Cancel", "btn-outline-danger", () => setModal("cancel")));
    }   
    return (
        <>
            <button type="button" className="btn btn-link text-decoration-none mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/tasks"))}>
                <FaArrowLeft /> Back to tasks
            </button>
            <div className="row">
                <div className="col-lg-8">
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                        <div className="card-body">
                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-4">
                                <span className="d-flex align-items-center justify-content-center rounded-circle bg-white shadow-sm flex-shrink-0" style={{ width: 72, height: 72, fontSize: 30 }}>
                                    {isDelivery ? <MdOutlineLocalShipping className="text-primary" /> : <MdOutlineAssignmentReturn className="text-primary" />}
                                </span>
                                <div className="pt-md-4 flex-grow-1 min-w-0">
                                    <h3 className="fw-bold mb-1">{task.taskType} task</h3>
                                    <small className="text-muted d-block mb-3 font-monospace">#{task.taskId}</small>
                                    <div className="d-flex flex-wrap gap-2">
                                        <Badge text={status.label} icon={status.icon} className={status.className} />
                                        <Badge text={task.staffId ? (task.staffName || task.staffId) : "Unassigned"} icon={<FaUserCheck />} className={task.staffId ? "badge-verified" : "badge-warning"} />
                                    </div>
                                </div>
                                <div className="d-flex flex-wrap gap-2 align-self-md-start mt-md-4">
                                    {actionItems}
                                </div>
                            </div>
                        </div>
                    </div>
                    {kycBlocked ? (
                        <div className="alert alert-warning d-flex align-items-start gap-2 mb-4" role="alert">
                            <span>{task.kycBlockReason || "The customer's KYC is not verified yet, so the car can't be delivered."}</span>
                        </div>
                    ) : null}
                    <DetailCard title="Task Details" icon={<FaRegClipboard />}>
                        <div className="info-grid px-1">
                            <Field label="Scheduled for">{formatDateTime(task.taskTime)}</Field>
                            <Field label="Hub">{task.hubId}</Field>
                            <Field label="Assigned to">{task.staffId ? `${task.staffName || ""} (${task.staffId})`.trim() : "Unassigned"}</Field>
                            <Field label="Assigned at">{task.assignedAt ? formatDateTime(task.assignedAt) : ""}</Field>
                            <Field label="Assigned by">{task.assignedBy}</Field>
                            <Field label="Started">{task.startTime ? formatDateTime(task.startTime) : ""}</Field>
                            <Field label="Finished">{task.endTime ? formatDateTime(task.endTime) : ""}</Field>
                            <Field label="Created">{formatDateTime(task.createdAt)}</Field>
                            {codeRequired && task.otpVerifiedAt ? <Field label="Customer code verified">{formatDateTime(task.otpVerifiedAt)}</Field> : null}
                            {task.overrideReason ? <Field label="Completed by override">{task.overrideReason}</Field> : null}
                            {task.errorMsg ? <Field label="Cancel reason">{task.errorMsg}</Field> : null}
                        </div>
                    </DetailCard>
                    <DetailCard title="Booking & Customer" icon={<FaCarSide />}>
                        {booking ? (
                            <>
                                <div className="info-grid px-1">
                                    <Field label="Booking">{task.bookingId ? (hasPermission("booking.view") ? <Link to={`/booking/details/${task.bookingId}`} className="text-primary">{task.bookingId}</Link> : task.bookingId) : ""}</Field>
                                    <Field label="Booking stage"><span className="text-capitalize">{booking.bookingStage}</span></Field>
                                    <Field label="Trip starts">{booking.startDate ? formatDateTime(booking.startDate) : ""}</Field>
                                    <Field label="Trip ends">{booking.endDate ? formatDateTime(booking.endDate) : ""}</Field>
                                    <Field label="Car">{task.car ? [task.car.registrationNo, task.car.variantName].filter(Boolean).join(" - ") : (task.carId || booking.carId)}</Field>
                                    <Field label="Car details">{task.car ? [task.car.year, task.car.color].filter(Boolean).join(" · ") : ""}</Field>
                                    {task.customer ? <Field label="Customer">{task.customer.name || task.customer.customerId}</Field> : null}
                                    {task.customer?.phone ? <Field label="Customer phone"><a href={`tel:${task.customer.phone}`} className="text-primary">+91 {task.customer.phone}</a></Field> : null}
                                </div>
                                {address ? (
                                    <div className="address-display mt-4">
                                        <h6 className="d-flex align-items-center gap-2"><IoLocation /> {isDelivery ? "Delivery address" : "Pick-up address"}</h6>
                                        <p className="m-0">{address?.address || address?.location?.address || "-"}</p>
                                        {mapsUrl(address) ? <a href={mapsUrl(address)} target="_blank" rel="noreferrer" className="d-inline-block mt-2 small">Open in Google Maps</a> : null}
                                    </div>
                                ) : null}
                            </>
                        ) : <p className="text-muted mb-0">No booking details are available for this task.</p>}
                    </DetailCard>
                    {hasPermission("task.track") && (hasPermission("task.viewAll") || isMine) ? <TaskTrackingPanel task={task} canRoute={hasPermission("staff.location.history")} /> : null}
                    <DetailCard title="Photos" icon={<FaCamera />}>
                        <div className="d-flex flex-column gap-4">
                            {PHOTO_SECTIONS.map((s) => (
                                <div key={s.key}>
                                    <div className="fw-semibold mb-2">{s.title} <span className="text-muted fw-normal">({photos[s.key].length})</span></div>
                                    {photos[s.key].length ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            {photos[s.key].map((url, i) => (
                                                <img
                                                    key={url}
                                                    src={url}
                                                    alt={`${s.title} ${i + 1}`}
                                                    className="cursor-pointer"
                                                    style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8 }}
                                                    onClick={() => setLightbox({ src: url, alt: `${s.title} ${i + 1}` })}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted small mb-0">
                                            {s.key === "endImages"
                                                ? (task.status === "completed" ? "No completion photos were saved for this task." : "Added when the task is completed.")
                                                : (["pending", "assigned"].includes(task.status) ? "Added when the task is started." : "No start photos were saved for this task.")}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </DetailCard>
                </div>
                <div className="col-lg-4">
                    <Timeline userLog={logs} className="mb-4" />
                </div>
            </div>
            {modal === "ring" ? <RingNowDialog taskId={task.taskId} statusUrl={`/staff/task/ring/${task.taskId}`} ringUrl={`/staff/task/ring/${task.taskId}`} onClose={savedModal} /> : ""}
            {modal === "assign" ? <TaskAssignModal taskId={task.taskId} hubId={task.hubId} currentStaffId={task.status === "assigned" ? task.staffId : ""} onClose={closeModal} onSaved={savedModal} /> : ""}
            {modal === "start" ? <TaskStartModal task={task} onClose={closeModal} onSaved={savedModal} /> : ""}
            {modal === "cancel" ? <TaskCancelModal taskId={task.taskId} onClose={closeModal} onSaved={savedModal} /> : ""}
            {modal === "release" ? <TaskReleaseModal taskId={task.taskId} onClose={closeModal} onSaved={savedModal} /> : ""}
            {modal === "complete" ? <TaskCompleteModal task={task} canOverride={hasPermission("task.override")} onClose={closeModal} onSaved={savedModal} /> : ""}

            {lightbox ? (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: "rgba(0,0,0,.8)", zIndex: 2000 }} onClick={() => setLightbox(null)} role="dialog" aria-label={lightbox.alt}>
                    <img src={lightbox.src} alt={lightbox.alt} style={{ maxWidth: "92vw", maxHeight: "90vh", borderRadius: 8 }} />
                </div>
            ) : null}
        </>
    );
};

export default TaskDetail;
